import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { BaseTexture } from '@babylonjs/core/Materials/Textures/baseTexture';
import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture';
import { HDRCubeTexture } from '@babylonjs/core/Materials/Textures/hdrCubeTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Color4, Quaternion, Vector3 } from '@babylonjs/core/Maths';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { LensRenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/lensRenderingPipeline';
import { SSAO2RenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/ssao2RenderingPipeline';
import { FxaaPostProcess } from '@babylonjs/core/PostProcesses/fxaaPostProcess';
import { Scene } from '@babylonjs/core/scene';
import { eBackground, eTextureSamplingMode } from '@models/classes/enums';
import { Kb3dObject } from '@models/classes/kb3d-object';
import { SceneNode, isSceneNode } from '@models/classes/scene-node';
import { Token } from '@models/classes/token';
import { ITrackedClass, isTrackedClass } from '@models/interfaces/tracked-class';
import { GuiHelper } from '@view/helpers/gui-helper';
import { KbViewer } from '@view/kb-viewer';
import { Renderer } from './renderer';
import { RendererManager } from './renderer-manager';

export const SKYBOX_SIZE = 1000;

export const SamplingModeMap = {
    [eTextureSamplingMode.nearest]: Texture.NEAREST_SAMPLINGMODE,
    [eTextureSamplingMode.bilinear]: Texture.BILINEAR_SAMPLINGMODE,
    [eTextureSamplingMode.trilinear]: Texture.TRILINEAR_SAMPLINGMODE,
};

export class SceneRenderer extends Renderer<SceneNode, TransformNode> {
    private ssaoPostProcess: SSAO2RenderingPipeline;
    private lensEffectPostProcess: LensRenderingPipeline;
    private fxaaPostProcess: FxaaPostProcess;
    private yVector = new Vector3(0, 1, 0);

    constructor(viewer: KbViewer, scene: Scene, private gui: GuiHelper, private renderManager: RendererManager) {
        super(viewer, scene);
    }

    modelToken = Token.SceneNode;
    handlesNode(node: Kb3dObject) {
        return isSceneNode(node) && !node.isNested();
    }

    update3dInternal(node: ITrackedClass<SceneNode>) {
        this.processEnvironment(node);

        //we create a transform node that ends up holding all space nodes in the scene
        //this creates parity with nested scenes and gives us something to transform in WebXR without re-parenting anything
        let tnode = this.get3d(node);
        if (!tnode) {
            tnode = new TransformNode(node._dynamicId, this.scene);
            this.set3d(node, tnode);

            if (!this.viewer.options.forceDisablePostProcess && this.scene.activeCamera) {
                if (node.enableFxaaPostProcess) {
                    this.fxaaPostProcess = new FxaaPostProcess(
                        'fxaa',
                        1.0,
                        this.scene.activeCamera,
                        Texture.BILINEAR_SAMPLINGMODE,
                        this.scene.getEngine()
                    );
                }
                if (node.enableLensEffectsPostProcess) {
                    this.lensEffectPostProcess = new LensRenderingPipeline(
                        'lens',
                        {
                            edge_blur: 0.5,
                            chromatic_aberration: 0.5,
                            distortion: 0, // Used to be 0.5 but lens distortion makes mouse selection useless,
                            dof_focus_distance: 200,
                            dof_aperture: 5.0, // set this very high for tilt-shift effect
                            grain_amount: 0.1,
                            dof_pentagon: true,
                            dof_gain: 1.0,
                            dof_threshold: 1.0,
                            dof_darken: 0,
                        },
                        this.scene,
                        1.0,
                        [this.scene.activeCamera]
                    );
                }
                if (node.enableSSAOPostProcess) {
                    this.ssaoPostProcess = new SSAO2RenderingPipeline('ssaopipeline', this.scene, 0.75);
                }
            }
        }

        if (node.changes.has('enableDeferredSceneUpdates')) {
            this.renderManager.maxRenderTime =
                node.enableDeferredSceneUpdates && !this.viewer.options.editorMode ? 20 : 20000;
        }

        if (node.changes.has('textureSamplingMode')) {
            const samplingMode = SamplingModeMap[node.textureSamplingMode];
            const materials = this.renderManager.material.getAllDefinedMaterials();

            materials.forEach(material => {
                material.getActiveTextures().forEach(texture => {
                    if (texture) {
                        texture.updateSamplingMode(samplingMode);
                    }
                });
            });
        }

        if (node.changes.has('backgroundColor') || node.changes.has('background')) {
            if (node.background == eBackground.color) {
                this.scene.clearColor = node.backgroundColor.toColor4();
            } else {
                this.scene.clearColor = new Color4(0, 0, 0, 0);
            }
        }
        if (node.changes.has('ambientColor')) {
            this.scene.ambientColor = node.ambientColor.toColor3();
        }
        if (node.changes.hasAny('renderMode', 'edgeWidth', 'edgeColor', 'edgeDisplayTolerance')) {
            this.viewer.setRenderMode(node.renderMode);
        }
        if (node.changes.has('enableHoverEffects')) {
            this.scene.constantlyUpdateMeshUnderPointer = node.enableHoverEffects;
        }
        if (node.changes.has('connectorSize')) {
            for (const c of node.getAllConnectors()) {
                this.viewer.rendererManager.connector.updateScale(c);
            }
            for (const c of node.getAllSketchControlPoints()) {
                this.viewer.rendererManager.controlPointRenderer.updateScale(c);
            }
        }
        if (node.changes.hasAny('dimensionUnitScalar', 'dimensionUnitSymbol')) {
            node.annotations.forEach(annotationNode => {
                if (isTrackedClass(annotationNode)) {
                    annotationNode.isModified = true;
                }
            });
        }

        let gridNeedsRebuild = false;
        if (
            node.changes.hasAny(
                '_forceGrid',
                'gridSize',
                'gridNormal',
                'gridStepSize',
                'gridMajorInterval',
                'backgroundColor'
            )
        ) {
            if (node._forceGrid || node.enableGrid) {
                this.gui.disableGrid();
                gridNeedsRebuild = true;
            }
            if (node._forceGrid || node.enableGridAxes) {
                this.gui.disableGridAxes();
                gridNeedsRebuild = true;
            }
        }
        if (gridNeedsRebuild || node.changes.has('enableGrid')) {
            if (node._forceGrid || node.enableGrid) {
                this.gui.enableGrid(node, node._forceGrid || node.enableGridAxes);
            } else {
                this.gui.disableGrid();
            }
        }
        if (gridNeedsRebuild || node.changes.has('enableGridAxes')) {
            if (node._forceGrid || node.enableGridAxes) {
                this.gui.enableGridAxes(node);
            } else {
                this.gui.disableGridAxes();
            }
        }

        if (node._rootScene?._dynamicId === node._dynamicId) {
            const eventPayload = Object.fromEntries(node.changes);
            if (this.viewer.canvas) {
                this.viewer.canvas.dispatchEvent(
                    new CustomEvent('kb3d.scenePropertyChange', { detail: eventPayload, bubbles: true })
                );
            }
        }
    }

    private _envTexture: BaseTexture;
    private _envTexturePath: string;
    public skybox: Mesh | null;

    processEnvironment(node: ITrackedClass<SceneNode>) {
        if (node.changes.has('environment') || node.changes.has('background') || node.changes.has('environmentBlur')) {
            const matService = node._manager?.getService(Token.MaterialService);
            if (!matService) {
                throw Error('Cannot fetch environment material');
            }

            let path: string;
            if (node.background === eBackground.environment) {
                if (node.environmentBlur < 0.15) {
                    path = matService.getAbsoluteEnvironmentPath(node.environment, 3);
                } else {
                    path = matService.getAbsoluteEnvironmentPath(node.environment, 1);
                }
            } else {
                path = matService.getAbsoluteEnvironmentPath(node.environment, 0);
            }

            if (path !== '' && this._envTexturePath !== path) {
                this._envTexturePath = path;
                if (this._envTexture) {
                    this._envTexture.dispose();
                }

                let texture: BaseTexture | undefined = undefined;

                if (path.match(/\.dds(\?.*)?$/) || path.match(/\.env(\?.*)?$/)) {
                    texture = CubeTexture.CreateFromPrefilteredData(path, this.scene);
                } else if (path.match(/\.hdr(\?.*)?$/)) {
                    texture = new HDRCubeTexture(path, this.scene, 128);
                }

                if (texture != null) {
                    texture.gammaSpace = false;
                    this.scene.environmentTexture = texture;
                    this._envTexture = texture;
                } else {
                    this.scene.environmentTexture = null;
                }
            }
        }

        if (
            (node.changes.has('background') || node.changes.has('environment')) &&
            node.background == eBackground.environment &&
            this._envTexture
        ) {
            if (!this.skybox) {
                this.skybox = this.scene.createDefaultSkybox(this._envTexture, true, SKYBOX_SIZE, node.environmentBlur);
            } else if (node.changes.has('environment')) {
                const skyMat = this.skybox.material as PBRMaterial;
                skyMat.reflectionTexture = this._envTexture.clone();
                skyMat.reflectionTexture!.coordinatesMode = Texture.SKYBOX_MODE;
            }
        }

        if (node.changes.has('background') && node.background != eBackground.environment) {
            if (this.skybox) {
                this.skybox.dispose();
                this.skybox = null;
            }
        }

        if (node.changes.has('environmentBlur')) {
            if (this.skybox) {
                const skyMat = this.skybox.material as PBRMaterial;
                skyMat.microSurface = 1.0 - node.environmentBlur;
            }
        }

        if (node.changes.has('environmentIntensity')) {
            this.scene.environmentIntensity = node.environmentIntensity;
        }

        if (node.changes.has('environmentRotation') && this.scene.environmentTexture) {
            if (
                this.scene.environmentTexture instanceof CubeTexture ||
                this.scene.environmentTexture instanceof HDRCubeTexture
            ) {
                this.scene.environmentTexture.rotationY = (node.environmentRotation * Math.PI) / 180;
                if (this.skybox) {
                    this.skybox.rotationQuaternion = Quaternion.RotationAxis(
                        this.yVector,
                        (node.environmentRotation * Math.PI) / 180
                    );
                }
            }
        }
    }

    public dispose() {
        super.dispose();
        if (this._envTexture) {
            this._envTexture.dispose();
        }
        if (this.skybox) {
            this.skybox.dispose();
        }
        if (this.ssaoPostProcess) {
            this.ssaoPostProcess.dispose();
        }
        if (this.lensEffectPostProcess) {
            this.lensEffectPostProcess.dispose();
        }
        if (this.fxaaPostProcess) {
            this.fxaaPostProcess.dispose();
        }
    }

    public getSkyBox() {
        return this.skybox;
    }
}
